/**
 * The frame every panel is drawn in: a scrolling body under one header.
 *
 * The header reads its own words from the registry rather than taking them at ten call sites, and
 * carries the ⓘ that opens the panel's guide. Panel guides open on CLICK ONLY — no first-visit
 * popup, no "seen" bookkeeping — which is the decision the Angular client landed on and the reason
 * this component owns the state rather than a service.
 *
 * APP-LOCAL COMPOSITION, not a design-system component, which is why `_design.md`'s components:
 * catalogue has no Panel entry and should not grow one. It is bound to this app's own furniture —
 * `registry.ts`, the store and `useLayout`'s master-detail rule — so there is no spec another app
 * could build against; what IS the system here is Card, Button and Text underneath it.
 */
import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { cn } from '@/lib/utils';
import { moduleById, type ScreenId } from '@/registry';
import { useStore } from '@/store';
import { useLayout } from '@/useLayout';
import * as React from 'react';
import { Pressable, ScrollView, View } from 'react-native';

/**
 * ONE COLUMN, ONE WIDTH, AND IT IS CENTRED.
 *
 * A single column does NOT run to the edge of a desktop window. A card stretched to 1150px is a
 * 200-character line of body copy, and "the stretched phone at 1600px" is the failure mode the
 * design file names by name. The cap is generous rather than narrow — Date Night's card wall and
 * the Scoreboard's stat tiles still get several across.
 *
 * It used to apply to the panel's CONTENT alone, which is how a 1280px window came to draw 877px
 * of cards under a full-width header, with the "Switch" button seven hundred pixels from the
 * title it belongs to and a Reconnecting alert wider than everything it was warning about. The
 * number lives here now, and the header, the guide, the body and `App.tsx`'s alert all take it,
 * so the window's leftover width is air on both sides rather than a gutter on one.
 *
 * Exported twice on purpose: the number for anything doing arithmetic, and the class list because
 * NativeWind needs a literal it can see at build time — `max-w-[${COLUMN_MAX}px]` compiles to
 * nothing at all.
 */
export const COLUMN_MAX = 880;
export const COLUMN_CLASS = 'w-full max-w-[880px] self-center';

/** About 68 characters of the `small` step — the measure prose stops being comfortable past.
 *  In px rather than `ch`, which is a CSS unit a phone has no idea what to do with. */
export const MEASURE_CLASS = 'max-w-[460px]';

export function Panel({
  id,
  actions,
  guideAction,
  detail,
  masterMax,
  bodyRef,
  children,
}: {
  id: ScreenId;
  /** The panel's own controls, in its header. One primary action per view, at most. */
  actions?: React.ReactNode;
  /**
   * One extra control inside the guide. Signals uses it to re-open its walkthrough: the guide is
   * already the "what is this panel for" place, so it is where somebody goes looking to be told
   * again — and a permanent button for that would be clutter in exchange for one use a year.
   */
  guideAction?: React.ReactNode;
  /**
   * The detail half of a master–detail panel, when something is selected. Master–detail is a
   * width decision and only a width decision: below bp-wide the detail replaces the list, at
   * bp-wide and above it sits beside it — off the same selection in the store, so neither
   * arrangement holds a truth the other does not.
   */
  detail?: React.ReactNode;
  /**
   * The panel's own scroller, for the one thing a screen cannot do without it: MOVE THE PAGE.
   *
   * Thanks needs it. Dropping yours is the moment the partner's card stops being a veil and starts
   * being their words, and a change that happens four hundred pixels below the fold is a change
   * that did not happen. The alternative was for each screen to grow its own ScrollView inside
   * this one, which is two scrollers on one page and a worse answer than one ref.
   *
   * It is the CHILDREN's scroller in both arrangements — the master column when a detail pane is
   * open beside it, the single column otherwise — so a caller's `scrollTo` always moves the list
   * it handed in, never the pane beside it.
   */
  bodyRef?: React.Ref<ScrollView>;
  /**
   * How wide the MASTER column may get once a detail pane is open beside it, in px.
   *
   * The default split is 2:3, which is right for a master whose rows carry a monogram, two lines
   * and a piece of metadata. It is wrong for a short index of one-line rows: Sync's rounds gave a
   * 600px column to a 60px row and left eighteen hundred pixels of empty column under it. A cap
   * here spends the leftover on the detail instead, which is the half of the screen that has
   * something to say.
   *
   * Ignored below bp-wide, where there is no split to divide.
   */
  masterMax?: number;
  children: React.ReactNode;
}) {
  const module = moduleById(id)!;
  const [guide, setGuide] = React.useState(false);
  const { wide } = useLayout();
  /**
   * The panel title is the one place the two looks cut Fraunces differently, and both sheets are
   * explicit about it: Bouquet sets it ITALIC — the warmth the sheet used to get from a script
   * face, at a size a phone can actually read — and Tuxedo sets it upright at `opsz` 144, which is
   * where Fraunces reaches the Didone stroke contrast that IS its signature object. Read from the
   * store rather than passed down, because a panel is drawn in a dozen screens and none of them
   * has an opinion about type.
   */
  const look = useStore((s) => s.look);

  /**
   * The cap applies to the ONE-column arrangement only. Where a detail pane is already open beside
   * the list, the two panes are what is using the width and a header pinned to 880 in front of
   * them would be a third measure on the same screen.
   */
  const column = wide && !detail ? COLUMN_CLASS : undefined;

  return (
    <View className="flex-1 gap-3">
      <View className={cn('flex-row flex-wrap items-start gap-2', column)}>
        <View className="min-w-0 grow basis-[180px]">
          <View className="flex-row items-center gap-2">
            {/* `fit-opsz` is web-only, and accepted as such: a static font instance has no `opsz`
                axis for a phone to move, so on native the class is inert and the title draws at
                Fraunces' default optical size. */}
            <Text variant="title" className={look === 'bouquet' ? 'italic' : 'fit-opsz'}>
              {module.heading}
            </Text>
            {/* The ⓘ stays a circle. Both sheets keep it: it is a shape — a dot you press — and
                not a corner the harder-corners graft has anything to say about. */}
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={`What is ${module.heading} for?`}
              onPress={() => setGuide((v) => !v)}
              className="border-line-strong size-6 items-center justify-center rounded-full border active:opacity-70">
              <Text variant="small" className="text-muted-foreground">
                ⓘ
              </Text>
            </Pressable>
          </View>
          <Text variant="small" className={cn('text-muted-foreground', MEASURE_CLASS)}>
            {module.sub}
          </Text>
        </View>
        {actions ? <View className="flex-row items-center gap-2">{actions}</View> : null}
      </View>

      {guide ? (
        <View className={column}>
          <PanelGuide id={id} action={guideAction} onClose={() => setGuide(false)} />
        </View>
      ) : null}

      {detail && wide ? (
        <View className="min-h-0 flex-1 flex-row gap-4">
          <Body ref={bodyRef} className="flex-[2]" style={masterMax ? { maxWidth: masterMax } : undefined}>
            {children}
          </Body>
          <View className="border-border min-h-0 flex-[3] border-l pl-4">
            <Body>{detail}</Body>
          </View>
        </View>
      ) : (
        <Body ref={bodyRef} contentClassName={column}>
          {detail ?? children}
        </Body>
      )}
    </View>
  );
}

function Body({
  className,
  contentClassName,
  children,
  ref,
  style,
}: {
  className?: string;
  contentClassName?: string;
  children: React.ReactNode;
  ref?: React.Ref<ScrollView>;
  style?: React.ComponentProps<typeof ScrollView>['style'];
}) {
  return (
    <ScrollView
      ref={ref}
      style={style}
      className={cn('flex-1', className)}
      contentContainerClassName={cn('gap-3 pb-6', contentClassName)}
      keyboardShouldPersistTaps="handled">
      {children}
    </ScrollView>
  );
}

/**
 * What this panel is for, how to play it, and the research that earned it a tab.
 *
 * Exported because the Signals WALKTHROUGH is a panel-shaped page that is not drawn by `Panel` —
 * it is an overlay — and its header carries the same ⓘ as every other panel's. One guide, opened
 * from both, rather than the overlay growing a second explanation of the same panel.
 */
export function PanelGuide({ id, action, onClose }: { id: ScreenId; action?: React.ReactNode; onClose: () => void }) {
  const module = moduleById(id)!;
  const { guide } = module;
  return (
    <View className="bg-card border-border gap-3 rounded-lg border p-4">
      <View className="flex-row items-start gap-3">
        <View className="bg-accent size-12 items-center justify-center rounded-lg">
          <Text className="text-accent-foreground text-[22px] leading-[26px]">{module.glyph}</Text>
        </View>
        <Text className="text-muted-foreground min-w-0 flex-1">{guide.objective}</Text>
      </View>

      <View className="gap-1.5">
        {guide.how.map((step, i) => (
          <View key={step} className="flex-row gap-2">
            <Text variant="small" className="text-primary font-semibold">
              {i + 1}.
            </Text>
            <Text variant="small" className="min-w-0 flex-1">
              {step}
            </Text>
          </View>
        ))}
      </View>

      <View className="border-border gap-1 border-t pt-3">
        <Text variant="small" className="text-muted-foreground">
          {guide.claim}
        </Text>
        {/* A CITATION IS A SENTENCE, not a column head. In tracked capitals "PLAY RESEARCH; FOX IN
            THE FOREST SCORING" was the loudest line in a card whose whole job is the paragraph
            above it. */}
        <Text variant="note">{guide.source}</Text>
      </View>

      <View className="flex-row flex-wrap items-center gap-2">
        <Button variant="ghost" size="sm" onPress={onClose}>
          <Text>Close</Text>
        </Button>
        {action}
      </View>
    </View>
  );
}
